/**
 * Available Batches Table Component
 * Table view of available batches with sortable columns and row actions
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import type { AvailableBatchResponse } from '@/types/availableBatches';
import type { BatchSort } from '@/types/availableBatches';
import { ProductTypeConfig, formatQuantity } from '@/types/inventory';
import { QualityBadge } from './QualityBadge';
import { StockStatusBadge } from './StockStatusBadge';
import {
  Eye,
  ShoppingCart,
  ArrowUpDown,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface AvailableBatchesTableProps {
  batches: AvailableBatchResponse[];
  sort?: BatchSort;
  onSortChange?: (sort: BatchSort) => void;
  onViewDetails?: (batch: AvailableBatchResponse) => void;
  onAllocateToOrder?: (batch: AvailableBatchResponse) => void;
  selectedBatches?: number[];
  onSelectionChange?: (batchIds: number[]) => void;
  className?: string;
}

export function AvailableBatchesTable({
  batches,
  sort,
  onSortChange,
  onViewDetails,
  onAllocateToOrder,
  selectedBatches = [],
  onSelectionChange,
  className
}: AvailableBatchesTableProps) {
  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? batches.map(b => b.id) : []);
    }
  };

  const handleSelectOne = (batchId: number, checked: boolean) => {
    if (onSelectionChange) {
      const newSelection = checked
        ? [...selectedBatches, batchId]
        : selectedBatches.filter(id => id !== batchId);
      onSelectionChange(newSelection);
    }
  };

  const handleSort = (field: BatchSort['field']) => {
    if (onSortChange && sort) {
      const newOrder = sort.field === field && sort.order === 'ASC' ? 'DESC' : 'ASC';
      onSortChange({ field, order: newOrder });
    }
  };

  const isAllSelected =
    batches.length > 0 && selectedBatches.length === batches.length;
  const isSomeSelected =
    selectedBatches.length > 0 && selectedBatches.length < batches.length;

  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            {onSelectionChange && (
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all batches"
                />
              </TableHead>
            )}
            <TableHead className="min-w-[140px]">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('expiryDate')}
                className="hover:bg-transparent font-semibold"
              >
                Batch #
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('availableQuantity')}
                className="hover:bg-transparent font-semibold float-right"
              >
                Available
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('qualityGrade')}
                className="hover:bg-transparent font-semibold"
              >
                Quality
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead className="text-right">Purity %</TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('expiryDate')}
                className="hover:bg-transparent font-semibold"
              >
                Expiry
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead className="text-center">Days Left</TableHead>
            <TableHead className="text-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('pricePerUnit')}
                className="hover:bg-transparent font-semibold float-right"
              >
                Price
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Stock Status</TableHead>
            <TableHead className="text-right w-16">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {batches.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={onSelectionChange ? 11 : 10}
                className="text-center py-8 text-gray-500"
              >
                No batches found
              </TableCell>
            </TableRow>
          ) : (
            batches.map(batch => {
              // Get product config with fallback for undefined or missing types
              const productConfig = ProductTypeConfig[batch.productType] || {
                icon: '📦',
                label: batch.productType || 'Unknown Product',
                abbreviation: 'UNK',
                bgColor: 'bg-gray-100',
                borderColor: 'border-gray-300'
              };

              const availablePercent = (batch.availableQuantity / batch.initialQuantity) * 100;
              const isExpiringSoon = batch.daysRemaining <= 30;
              const isSelected = selectedBatches.includes(batch.id);

              return (
                <TableRow
                  key={batch.id}
                  className={cn(
                    'hover:bg-gray-50',
                    isSelected && 'bg-blue-50'
                  )}
                >
                  {onSelectionChange && (
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleSelectOne(batch.id, checked as boolean)
                        }
                        aria-label={`Select batch ${batch.batchNumber}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {batch.batchNumber}
                      {batch.pesoCompliant && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                          <Shield className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{productConfig.icon}</span>
                      <div>
                        <div className="text-sm font-medium">
                          {productConfig.abbreviation}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatQuantity(batch.availableQuantity, batch.quantityUnit)}
                  </TableCell>
                  <TableCell>
                    <QualityBadge
                      grade={batch.qualityGrade}
                      purity={batch.purityPercentage}
                      showPurity={false}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {batch.purityPercentage.toFixed(1)}%
                  </TableCell>
                  <TableCell>
                    <span className={cn(
                      'text-sm',
                      isExpiringSoon ? 'text-orange-600 font-medium' : 'text-gray-600'
                    )}>
                      {format(new Date(batch.expiryDate), 'MMM dd, yyyy')}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className={cn(
                        isExpiringSoon
                          ? 'bg-orange-50 text-orange-700 border-orange-300'
                          : 'bg-gray-50 text-gray-700 border-gray-300'
                      )}
                    >
                      {batch.daysRemaining}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    ₹{batch.pricePerUnit.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[150px] truncate">
                    {batch.storageLocation}
                  </TableCell>
                  <TableCell>
                    <StockStatusBadge
                      level={batch.stockLevel}
                      availablePercent={availablePercent}
                      showIcon={true}
                      showPercentage={true}
                      size="md"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {onViewDetails && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onViewDetails(batch)}
                          className="h-8 w-8 p-0 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                          title="View batch details"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="sr-only">View Details</span>
                        </Button>
                      )}
                      {onAllocateToOrder && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onAllocateToOrder(batch)}
                          disabled={batch.availableQuantity === 0}
                          className="h-8 w-8 p-0 hover:bg-green-50 hover:border-green-300 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={batch.availableQuantity === 0 ? "No quantity available" : "Allocate to order"}
                        >
                          <ShoppingCart className="h-4 w-4" />
                          <span className="sr-only">Allocate to Order</span>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
